// Astrology lookup tables — Unicode glyphs and signs

export const PLANET_GLYPHS: Record<string, string> = {
  Sun: "☉",        // ☉
  Moon: "☽",       // ☽
  Mercury: "☿",    // ☿
  Venus: "♀",      // ♀
  Mars: "♂",       // ♂
  Jupiter: "♃",    // ♃
  Saturn: "♄",     // ♄
  Rahu: "☊",       // ☊ (north node)
  Ketu: "☋",       // ☋ (south node)
  Ascendant: "Asc",
};

export const SIGN_GLYPHS: Record<string, string> = {
  Aries: "♈",       // ♈
  Taurus: "♉",      // ♉
  Gemini: "♊",      // ♊
  Cancer: "♋",      // ♋
  Leo: "♌",         // ♌
  Virgo: "♍",       // ♍
  Libra: "♎",       // ♎
  Scorpio: "♏",     // ♏
  Sagittarius: "♐", // ♐
  Capricorn: "♑",   // ♑
  Aquarius: "♒",    // ♒
  Pisces: "♓",      // ♓
};

export const SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

export const SIGN_INDEX: Record<string, number> = SIGNS.reduce(
  (acc, s, i) => ({ ...acc, [s]: i }),
  {}
);

// Benefic / malefic classification (rough — Moon depends on phase)
export const BENEFIC_PLANETS = new Set(["Jupiter", "Venus"]);
export const MALEFIC_PLANETS = new Set(["Saturn", "Mars", "Rahu", "Ketu", "Sun"]);

export function planetClass(name: string): "benefic" | "malefic" | "neutral" {
  if (BENEFIC_PLANETS.has(name)) return "benefic";
  if (MALEFIC_PLANETS.has(name)) return "malefic";
  return "neutral";
}

// ---- Dignity (Step 1 B2) ----------------------------------------------------
// Mirrors backend `pillars.planet_dignity.planet_dignity_score()` tier output.
// The backend enriches `report.planets[i].dignity` directly so most code reads
// `p.dignity`. This client-side helper is the fallback / canonical reference:
// if you only have `(planet, sign)` and no enriched payload, call this.
//
// Source: BPHS Ch. 3 v.30-32 (exaltation, mooltrikona, own sign), v.55-60
// (naisargika maitri). Naisargika table from `pillars/planet_dignity.py`.
const OWN_SIGNS: Record<string, ReadonlySet<string>> = {
  Sun:     new Set(["Leo"]),
  Moon:    new Set(["Cancer"]),
  Mars:    new Set(["Aries", "Scorpio"]),
  Mercury: new Set(["Gemini", "Virgo"]),
  Jupiter: new Set(["Sagittarius", "Pisces"]),
  Venus:   new Set(["Taurus", "Libra"]),
  Saturn:  new Set(["Capricorn", "Aquarius"]),
};
const EXALT_SIGN: Record<string, string> = {
  Sun: "Aries", Moon: "Taurus", Mars: "Capricorn", Mercury: "Virgo",
  Jupiter: "Cancer", Venus: "Pisces", Saturn: "Libra",
  Rahu: "Taurus", Ketu: "Scorpio",
};
const FRIENDS: Record<string, ReadonlySet<string>> = {
  Sun: new Set(["Moon", "Mars", "Jupiter"]),
  Moon: new Set(["Sun", "Mercury"]),
  Mars: new Set(["Sun", "Moon", "Jupiter"]),
  Mercury: new Set(["Sun", "Venus"]),
  Jupiter: new Set(["Sun", "Moon", "Mars"]),
  Venus: new Set(["Mercury", "Saturn"]),
  Saturn: new Set(["Mercury", "Venus"]),
};
const ENEMIES: Record<string, ReadonlySet<string>> = {
  Sun: new Set(["Venus", "Saturn"]),
  Mars: new Set(["Mercury"]),
  Mercury: new Set(["Moon"]),
  Jupiter: new Set(["Mercury", "Venus"]),
  Venus: new Set(["Sun", "Moon"]),
  Saturn: new Set(["Sun", "Moon", "Mars"]),
};
export const SIGN_LORD: Record<string, string> = {
  Aries: "Mars", Taurus: "Venus", Gemini: "Mercury", Cancer: "Moon",
  Leo: "Sun", Virgo: "Mercury", Libra: "Venus", Scorpio: "Mars",
  Sagittarius: "Jupiter", Capricorn: "Saturn", Aquarius: "Saturn", Pisces: "Jupiter",
};

export type PlanetDignityTier =
  | "exalted" | "mooltrikona" | "own" | "friend" | "neutral" | "enemy" | "debilitated" | "unknown";

/**
 * Compute classical BPHS-based dignity tier for a planet placed in a sign.
 * Used as a fallback when `p.dignity` is not present on the planet emit.
 * Note: tier-within-exaltation-degrees (the linear taper) is not implemented
 * client-side; backend returns the precise tier. For rough UI, treat
 * "exalted" / "debilitated" as ±15° of the deep degree.
 */
export function getDignity(planet: string, sign: string | null | undefined): PlanetDignityTier {
  if (!planet || !sign) return "unknown";
  if (EXALT_SIGN[planet] === sign) return "exalted";
  // Debilitation = sign opposite the exaltation, same deep degree
  const exSign = EXALT_SIGN[planet];
  if (exSign) {
    const idx = SIGNS.indexOf(exSign);
    if (idx >= 0) {
      const debilSign = SIGNS[(idx + 6) % 12];
      if (debilSign === sign) return "debilitated";
    }
  }
  // Own sign
  if (OWN_SIGNS[planet]?.has(sign)) return "own";
  // Friendship / enmity via sign lord
  const lord = SIGN_LORD[sign];
  if (lord) {
    if (FRIENDS[planet]?.has(lord)) return "friend";
    if (ENEMIES[planet]?.has(lord)) return "enemy";
  }
  // Rahu/Ketu have no classical naisargika — fall through to neutral
  return "neutral";
}

/** Resolve a planet's dignity with priority: explicit `p.dignity` field, else fallback. */
export function resolveDignity(
  planet: { name: string; sign: string | null; dignity?: string | null },
): PlanetDignityTier {
  if (planet.dignity && planet.dignity !== "unknown") {
    return planet.dignity as PlanetDignityTier;
  }
  return getDignity(planet.name, planet.sign);
}

/** Human-readable dignity badge metadata. */
export function dignityMeta(tier: PlanetDignityTier): { label: string; cls: string } {
  switch (tier) {
    case "exalted":     return { label: "Exalted",     cls: "dignity-exalted" };
    case "mooltrikona": return { label: "Moolatrikona", cls: "dignity-mooltrikona" };
    case "own":         return { label: "Own sign",    cls: "dignity-own" };
    case "friend":      return { label: "Friend",      cls: "dignity-friend" };
    case "neutral":     return { label: "Neutral",     cls: "dignity-neutral" };
    case "enemy":       return { label: "Enemy",       cls: "dignity-enemy" };
    case "debilitated": return { label: "Debilitated", cls: "dignity-debilitated" };
    default:            return { label: "—",           cls: "dignity-unknown" };
  }
}
